import { useEffect, useRef } from 'react';
import { FieldValues, Path, UseFormReturn } from 'react-hook-form';

export function useServerWarnings<TFormValues extends FieldValues>(
  methods: UseFormReturn<TFormValues>,
  warnings: Record<string, string> | undefined,
  validFields: Path<TFormValues>[]
) {
  const appliedRef = useRef<Record<string, string>>({});
  const { touchedFields, dirtyFields, isSubmitted, errors } = methods.formState;

  useEffect(() => {
    if (!warnings) return;

    const hasTouchedFields = Object.keys(touchedFields ?? {}).length > 0;
    if (!hasTouchedFields && !isSubmitted) return;

    const nestedValue = (record: unknown, path: string): unknown =>
      path
        .split('.')
        .reduce<unknown>(
          (current, segment) =>
            current && typeof current === 'object'
              ? (current as Record<string, unknown>)[segment]
              : undefined,
          record
        );

    Object.entries(warnings).forEach(([key, value]) => {
      if (!validFields.includes(key as Path<TFormValues>)) return;
      const fieldName = key as Path<TFormValues>;
      const fieldError = nestedValue(errors, key) as { message?: string } | undefined;
      const fieldIsDirty = Boolean(nestedValue(dirtyFields, key));

      if (fieldIsDirty) {
        if (appliedRef.current[key] === value && fieldError?.message === value) {
          methods.clearErrors(fieldName);
        }
        delete appliedRef.current[key];
        return;
      }

      // Already applied this exact warning
      if (appliedRef.current[key] === value) return;
      // Don't overwrite an existing Zod validation error
      if (fieldError) return;

      methods.setError(fieldName, { message: value });
      appliedRef.current[key] = value;
    });
  }, [warnings, touchedFields, dirtyFields, isSubmitted, validFields, methods, errors]);
}
